const API_ENDPOINTS = {
  waitlist: import.meta.env.VITE_WAITLIST_SCRIPT_URL || '',
  newsletter: import.meta.env.VITE_NEWSLETTER_SCRIPT_URL || '',
  contact: import.meta.env.VITE_CONTACT_SCRIPT_URL || ''
};

interface ApiResponse {
  status: 'success' | 'error';
  message: string;
}

export const submitToGoogleScript = async (
  endpoint: keyof typeof API_ENDPOINTS,
  data: Record<string, unknown>
): Promise<ApiResponse> => {
  const scriptUrl = API_ENDPOINTS[endpoint];
  
  // Fallback to simulated behavior if no URL is configured
  if (!scriptUrl) {
    console.warn(`No API URL configured for ${endpoint}. Using simulated response.`);
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      status: 'success',
      message: 'Form submitted successfully! (Simulated)'
    };
  }
  
  try {
    const response = await fetch(scriptUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/plain;charset=utf-8', // CRITICAL: Must be text/plain to avoid CORS
      },
      body: JSON.stringify(data),
      redirect: 'follow' // CRITICAL: Apps Script redirects responses
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('API Error:', error);
    
    // Provide a user-friendly error message
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw new Error(`Failed to submit form: ${error.message}`);
    }
    
    throw new Error('Failed to submit form. Please try again.');
  }
};

// Helper function to create timeout wrapper for fetch
export const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeout = 30000 // 30 seconds default
): Promise<Response> => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  }
};

// Alternative version with timeout support
export const submitToGoogleScriptWithTimeout = async (
  endpoint: keyof typeof API_ENDPOINTS,
  data: Record<string, unknown>,
  timeout = 30000
): Promise<ApiResponse> => {
  const scriptUrl = API_ENDPOINTS[endpoint];
  
  // Fallback to simulated behavior if no URL is configured
  if (!scriptUrl) {
    console.warn(`No API URL configured for ${endpoint}. Using simulated response.`);
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      status: 'success',
      message: 'Form submitted successfully! (Simulated)'
    };
  }
  
  try {
    const response = await fetchWithTimeout(
      scriptUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        body: JSON.stringify(data),
        redirect: 'follow'
      },
      timeout
    );
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('API Error:', error);
    
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      if (error.message.includes('timed out')) {
        throw new Error('Request timed out. Please try again.');
      }
      throw new Error(`Failed to submit form: ${error.message}`);
    }
    
    throw new Error('Failed to submit form. Please try again.');
  }
};